import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Send, MessageSquare, Sparkles } from 'lucide-react';
import emailjs from '@emailjs/browser';

const Contact = () => {
  const formRef = useRef<HTMLFormElement>(null);
  const [formData, setFormData] = useState({
    user_name: '',
    user_email: '',
    message: '',
    contact_number: '0'
  });
  
  const [status, setStatus] = useState({
    submitting: false,
    submitted: false,
    error: null as string | null
  });

  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      contact_number: String(Math.random() * 100000 | 0)
    }));
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formRef.current) return;

    setStatus({ submitting: true, submitted: false, error: null });

    try {
      // Debug line to see exactly what we're getting
      console.log('Raw public key:', process.env.NEXT_PUBLIC_EMAILJS_PUBLIC_KEY);

      // Your hardcoded values since they work
      await emailjs.send(
        'service_sp44yoz',  
        'template_pbnxjly', 
        {
          to_name: 'Admin', // Or whatever name you want to receive it as
          name: formData.user_name,
          email: formData.user_email, // Add this for the sender's email
          message: formData.message,
          contact_number: formData.contact_number
        },
        'i-JKef_umdz5iFPir'  // Try hardcoding your public key here to test
      );

      setStatus({ submitting: false, submitted: true, error: null });
      setFormData({
        user_name: '',
        user_email: '',
        message: '',
        contact_number: String(Math.random() * 100000 | 0)
      });
      
      setTimeout(() => {
        setStatus(prev => ({ ...prev, submitted: false }));
      }, 3000);

    } catch (error) {
      console.error('Failed to send email:', error);
      const errorMessage = error instanceof Error 
        ? error.message 
        : 'Failed to send message. Please try again.';
      
      setStatus({ 
        submitting: false, 
        submitted: false, 
        error: errorMessage
      });
    }
  };

  return (
    <div className="relative min-h-screen bg-[#b3ebf2] overflow-hidden px-4 sm:px-6">
      <motion.div className="absolute inset-0">
        <motion.div 
          className="absolute -top-40 -right-40 w-60 sm:w-80 h-60 sm:h-80 bg-[#ffd54f] rounded-full mix-blend-multiply filter blur-xl opacity-30"
          animate={{ scale: [1, 1.2, 1], rotate: [0, 90, 0] }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        />
        <motion.div 
          className="absolute -bottom-40 -left-40 w-60 sm:w-80 h-60 sm:h-80 bg-[#81deea] rounded-full mix-blend-multiply filter blur-xl opacity-30"
          animate={{ scale: [1.2, 1, 1.2], rotate: [90, 0, 90] }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear", delay: 10 }}
        />
      </motion.div>

      <motion.div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.02)_2px,transparent_2px),linear-gradient(90deg,rgba(0,0,0,0.02)_2px,transparent_2px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]"/>

      <div className="relative max-w-7xl mx-auto pt-20 sm:pt-32 pb-16">
        <motion.div className="flex justify-center mb-12">
          <motion.div 
            className="bg-[#ffd54f]/10 rounded-full p-1 pr-4 sm:pr-6 flex items-center gap-2 border border-[#ffd54f]/20"
            whileHover={{ scale: 1.05, y: -2 }}
          >
            <motion.span 
              className="bg-[#ffd54f] rounded-full p-1"
              animate={{ rotate: 360 }}
              transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
            >
              <MessageSquare className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
            </motion.span>
            <span className="text-xs sm:text-sm text-[#f4b400] font-medium">Get in Touch With Us</span>
          </motion.div>
        </motion.div>

        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-6xl font-bold tracking-tight">
            <motion.span 
              className="block text-gray-900"
              whileHover={{ scale: 1.02 }}
            >
              Let's Start a
            </motion.span>
            <motion.span 
              className="block text-[#f4b400] mt-2"
              whileHover={{ scale: 1.02 }}
            >
              Conversation
            </motion.span>
          </h1>
        </div>

        <motion.form 
          ref={formRef}
          id="contact-form"
          onSubmit={handleSubmit}
          className="max-w-2xl mx-auto space-y-6 relative bg-white/70 backdrop-blur-lg p-8 rounded-2xl shadow-lg"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <input type="hidden" name="contact_number" value={formData.contact_number} />

          {status.submitted && (
            <motion.div 
              className="absolute top-4 left-4 right-4 bg-green-100 text-green-700 p-3 rounded-lg"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              Message sent successfully!
            </motion.div>
          )}

          {status.error && (
            <motion.div 
              className="absolute top-4 left-4 right-4 bg-red-100 text-red-700 p-3 rounded-lg"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {status.error}
            </motion.div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
              <input
                type="text"
                name="user_name"
                value={formData.user_name}
                onChange={(e) => setFormData({ ...formData, user_name: e.target.value })}
                className="w-full p-3 rounded-lg border-2 border-[#81deea] focus:border-[#ffd54f] focus:ring-2 focus:ring-[#ffd54f]/50 transition-colors"
                required
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
            >
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <input
                type="email"
                name="user_email"
                value={formData.user_email}
                onChange={(e) => setFormData({ ...formData, user_email: e.target.value })}
                className="w-full p-3 rounded-lg border-2 border-[#81deea] focus:border-[#ffd54f] focus:ring-2 focus:ring-[#ffd54f]/50 transition-colors"
                required
              />
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
            <textarea
              name="message"
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              className="w-full p-3 rounded-lg border-2 border-[#81deea] focus:border-[#ffd54f] focus:ring-2 focus:ring-[#ffd54f]/50 transition-colors h-32"
              required
            />
          </motion.div>

          <motion.button
            type="submit"
            disabled={status.submitting}
            className="w-full group relative px-6 py-3 bg-[#ffd54f] text-white rounded-full text-lg font-medium overflow-hidden flex items-center justify-center gap-2 disabled:opacity-70"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {status.submitting ? (
              <>
                <motion.div 
                  className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                />
                Sending...
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                Send Message
                <motion.span
                  className="absolute right-4"
                  animate={{ x: [-16, -14] }}
                  transition={{ duration: 0.6, repeat: Infinity, repeatType: "reverse" }}
                >
                  <Sparkles className="w-4 h-4" />
                </motion.span>
              </>
            )}
          </motion.button>
        </motion.form>
      </div>
    </div>
  );
};

export default Contact;